import { Ref, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { StanButton, StanInput, StanLoader, StanModal } from '@/components';
import IconImage from '@/assets/icons/ic-image.svg';
import IconSearch from '@/assets/icons/ic-search.svg';
import { StanFileUpload, StanFileUploadRef } from '../StanFileUpload/StanFileUpload';
import type { StanUnsplashImageSelectorProps, StanUnsplashImageSelectorRef } from './StanUnsplashImageSelector.types';
import { useUnsplashImageSelector } from './StanUnsplashImageSelector.logic';
import { BareSvg, cn, stanNotify } from '@/lib';
export type { StanUnsplashImageSelectorProps, StanUnsplashImageSelectorRef };

export const StanUnsplashImageSelector = ({
    isOpen,
    onClose,
    onImagePicked,
    ref,
}: StanUnsplashImageSelectorProps & { ref?: Ref<StanUnsplashImageSelectorRef> }) => {
    const fileUploadRef = useRef<StanFileUploadRef>(null);
    const acceptedFileTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp', 'image/gif'];

    const {
        search,
        setSearch,
        choosingImage,
        setChoosingImage,
        isLoadingUnsplashImage,
        images,
        loading,
        isFetchingNextPage,
        hasNextPage,
        init,
        handleNextPage,
        handleImageSelected,
        handleVisitUser,
        reset,
    } = useUnsplashImageSelector(onImagePicked);

    useImperativeHandle(
        ref,
        () =>
            ({
                init,
            }) as StanUnsplashImageSelectorRef
    );

    useEffect(() => {
        return () => {
            reset();
        };
    }, [isOpen, reset]);

    const handleClose = useCallback(() => {
        if (!choosingImage) {
            onClose();
        }
        setChoosingImage(false);
    }, [choosingImage, onClose, setChoosingImage]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0 && acceptedFiles[0]) {
                onImagePicked?.(acceptedFiles[0]);
            } else {
                stanNotify({
                    type: 'error',
                    title: 'Please select a valid image.',
                });
            }
        },
        [onImagePicked]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        noClick: true,
        noKeyboard: true,
        accept: { [acceptedFileTypes.join(',')]: [] },
    });

    const fileUploadComponent = (
        <StanFileUpload
            ref={fileUploadRef}
            accept={acceptedFileTypes.join(',')}
            multiple={false}
            confirmUpload
            objectType='unknown'
            onFilePicked={data => {
                if (!data[0]) return;
                onImagePicked?.(data[0]);
            }}
        />
    );

    return (
        <StanModal
            isOpen={isOpen}
            title='Choose Your Image'
            id='unsplash-image-selector'
            onClose={handleClose}
            size='lg'
        >
            {fileUploadComponent}
            <div {...getRootProps()} className='relative h-[min(90vh,900px)]'>
                <input {...getInputProps()} />

                {/* Drag overlay */}
                {isDragActive && (
                    <div className='animate-in fade-in absolute inset-0 z-50 flex items-center justify-center'>
                        <div className='text-brand-main flex flex-col items-center gap-4'>
                            <BareSvg SvgComponent={IconImage} className='size-16' removeFill />
                            <div className='text-body-md text-brand-main'>Upload Image</div>
                        </div>
                    </div>
                )}

                <div className={cn(isDragActive ? 'animate-out fade-out fill-mode-forwards' : 'animate-in fade-in')}>
                    <div className='mb-4'>
                        <StanInput
                            placeholder='Search Keywords'
                            name='search'
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            startIcon={IconSearch}
                        />
                    </div>

                    {isLoadingUnsplashImage && (
                        <div className='flex items-center justify-center p-4'>
                            <StanLoader />
                        </div>
                    )}
                    {!isLoadingUnsplashImage && (
                        <>
                            <div className='grid grid-cols-2 gap-4 lg:grid-cols-3'>
                                <div className='relative flex h-40 max-h-40 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-md border-[1px] border-[var(--border-color-disabled)]'>
                                    <div className='text-body-md text-gray flex flex-col items-center gap-2'>
                                        <div className='text-body-md text-gray'>Drag Image Here</div>
                                        <div className='text-body-sm text-gray'>OR</div>
                                    </div>
                                    <StanButton
                                        type='button'
                                        name='upload-image'
                                        size='sm'
                                        variant='outline'
                                        onClick={() => fileUploadRef.current?.onPickFile()}
                                        icon={IconImage}
                                    >
                                        Upload Image
                                    </StanButton>
                                </div>

                                {images.map(image => (
                                    <div
                                        key={image.id}
                                        className='relative h-40 max-h-40 cursor-pointer overflow-hidden rounded-md'
                                    >
                                        <img
                                            src={image.urls.small}
                                            className='h-full w-full object-cover transition-transform hover:scale-105'
                                            onClick={() => handleImageSelected(image)}
                                            alt='Unsplash'
                                        />
                                        <div
                                            className='absolute bottom-2 left-2 rounded-md bg-black/50 px-2 py-1 text-xs text-white'
                                            onClick={() => handleVisitUser(image)}
                                        >
                                            {image.user.first_name} {image.user.last_name}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className='flex items-center justify-center p-4'>
                                {!isFetchingNextPage && loading && <StanLoader />}
                                {(hasNextPage || (isFetchingNextPage && loading)) && (
                                    <StanButton
                                        type='button'
                                        name='load-more-unsplash'
                                        variant='outline'
                                        onClick={handleNextPage}
                                        loading={loading}
                                    >
                                        Load More
                                    </StanButton>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </StanModal>
    );
};

StanUnsplashImageSelector.displayName = 'StanUnsplashImageSelector';
